/**
 * Layers data for the MTG Rules Reference.
 * Defines the 7-layer system used to determine how continuous
 * effects interact on the battlefield, including layer 7 sublayers.
 *
 * CommonJS module for compatibility with both backend (require) and
 * frontend (import via Webpack/Babel interop).
 *
 * @module rules/layerData
 */

const LAYER_NUMBERS = {
  COPY: 1,
  CONTROL: 2,
  TEXT: 3,
  TYPE: 4,
  COLOR: 5,
  ABILITY: 6,
  POWER_TOUGHNESS: 7,
};

const SUBLAYER_IDS = {
  CDA: '7a',
  SET: '7b',
  MODIFY: '7c',
  COUNTERS: '7d',
  SWITCH: '7e',
};

const LAYERS = [
  {
    number: LAYER_NUMBERS.COPY,
    name: 'Copy Effects',
    description:
      'Copy effects are applied first. When a permanent enters the battlefield as a copy of another object, or a spell or ability makes something a copy, the copiable values (name, mana cost, color, type, abilities, power/toughness) are established in this layer. Subsequent layers then modify these copied characteristics.',
    examples: [
      {
        scenario: 'Clone enters the battlefield as a copy of Tarmogoyf.',
        resolution:
          'In Layer 1, Clone copies all copiable characteristics of Tarmogoyf: its name, mana cost, type line, abilities (including the characteristic-defining ability for P/T), and base power/toughness. Clone is now a Tarmogoyf in all respects except it retains no memory of being a Clone.',
      },
      {
        scenario: 'Clever Impersonator copies a Baneslayer Angel that has been enchanted by Pacifism.',
        resolution:
          'In Layer 1, Clever Impersonator copies only the copiable values of Baneslayer Angel: name, cost, types, abilities, and P/T. The Pacifism enchantment is NOT copied because aura attachments and external effects are not copiable values.',
      },
    ],
  },
  {
    number: LAYER_NUMBERS.CONTROL,
    name: 'Control-Changing Effects',
    description:
      'Effects that change which player controls a permanent are applied in this layer. If multiple control-changing effects apply to the same permanent, they are applied in timestamp order, and the most recent one "wins."',
    examples: [
      {
        scenario: 'Player A controls a Shivan Dragon. Player B casts Control Magic targeting it. Later, Player A casts Homeward Path to return it.',
        resolution:
          'In Layer 2, Control Magic\'s effect (timestamp T1) gives Player B control. Later, Homeward Path\'s effect (timestamp T2) returns control to Player A. Since T2 is more recent, Player A controls Shivan Dragon.',
      },
      {
        scenario: 'Player A casts Bribery and puts Agent of Treachery from Player B\'s library onto the battlefield under Player A\'s control.',
        resolution:
          'In Layer 2, the Bribery effect establishes Player A as the controller. Agent of Treachery enters under Player A\'s control and its enter-the-battlefield trigger is controlled by Player A.',
      },
    ],
  },
  {
    number: LAYER_NUMBERS.TEXT,
    name: 'Text-Changing Effects',
    description:
      'Effects that change the text of a permanent are applied in this layer. This includes effects that change what color words, basic land type words, or creature type words appear in a card\'s rules text. Text-changing effects are relatively rare in modern Magic.',
    examples: [
      {
        scenario: 'Magical Hack changes "swampwalk" to "forestwalk" on a creature.',
        resolution:
          'In Layer 3, the word "swamp" in "swampwalk" is changed to "forest," making the ability "forestwalk." This change happens before types, abilities, and P/T are determined in later layers.',
      },
      {
        scenario: 'Sleight of Mind changes "protection from black" on a White Knight to "protection from red."',
        resolution:
          'In Layer 3, the color word "black" is changed to "red" in the text. When Layer 6 processes abilities, the creature now has "protection from red" instead of "protection from black."',
      },
    ],
  },
  {
    number: LAYER_NUMBERS.TYPE,
    name: 'Type-Changing Effects',
    description:
      'Effects that change a permanent\'s card type, subtype, or supertype are applied here. This includes effects that make lands into creatures, turn artifacts into enchantments, or add/remove creature types. Blood Moon and similar effects operate in this layer.',
    examples: [
      {
        scenario: 'Blood Moon is on the battlefield. Player controls a Steam Vents (a nonbasic land with types "Island Mountain").',
        resolution:
          'In Layer 4, Blood Moon\'s effect removes all land subtypes from nonbasic lands and makes them Mountains. Steam Vents loses "Island" and gains (retains) "Mountain." Because it is now only a Mountain, it can only tap for red mana (basic land types grant intrinsic mana abilities in Layer 6).',
      },
      {
        scenario: 'Humility is on the battlefield. A player casts Animate Dead on a Craterhoof Behemoth in the graveyard.',
        resolution:
          'In Layer 4, Animate Dead makes Craterhoof Behemoth an enchantment in addition to its other types (if applicable). Type changes happen before ability removal in Layer 6.',
      },
    ],
  },
  {
    number: LAYER_NUMBERS.COLOR,
    name: 'Color-Changing Effects',
    description:
      'Effects that change a permanent\'s color are applied in this layer. This includes effects that add colors, remove colors, or set a permanent to a specific color. A card\'s printed color (from mana cost or color indicator) is its default before this layer applies.',
    examples: [
      {
        scenario: 'Painter\'s Servant is on the battlefield, naming blue. An opponent controls a Tarmogoyf (green).',
        resolution:
          'In Layer 5, Painter\'s Servant makes all cards everywhere blue in addition to their other colors. Tarmogoyf is now both green and blue.',
      },
      {
        scenario: 'Darkest Hour is on the battlefield ("All creatures are black"). A player controls a White Knight.',
        resolution:
          'In Layer 5, Darkest Hour overrides White Knight\'s color. White Knight is now black instead of white. Note this does not change its mana cost or the mana symbols on the card.',
      },
    ],
  },
  {
    number: LAYER_NUMBERS.ABILITY,
    name: 'Ability-Adding and Ability-Removing Effects',
    description:
      'Effects that add or remove abilities from permanents are applied here. This includes granting keywords (like flying or hexproof), removing all abilities (like Humility or Overwhelming Splendor), and adding activated or triggered abilities. Effects are applied in timestamp order within this layer.',
    examples: [
      {
        scenario: 'Humility is on the battlefield ("All creatures lose all abilities and have base power and toughness 1/1"). A creature has been enchanted with Rancor ("Enchanted creature gets +2/+0 and has trample").',
        resolution:
          'In Layer 6, Humility (timestamp T1) removes all abilities, then Rancor (timestamp T2, if played after Humility) adds trample. The creature has trample. Humility\'s P/T effect is applied in Layer 7, and Rancor\'s +2/+0 is also applied in Layer 7.',
      },
      {
        scenario: 'A player controls Akroma, Angel of Wrath. An opponent plays Sudden Spoiling ("Until end of turn, creatures target player controls lose all abilities and have base power and toughness 0/2").',
        resolution:
          'In Layer 6, Sudden Spoiling removes all of Akroma\'s abilities (flying, first strike, vigilance, trample, haste, protection from black and red). Its P/T change is applied in Layer 7.',
      },
    ],
  },
  {
    number: LAYER_NUMBERS.POWER_TOUGHNESS,
    name: 'Power and Toughness',
    description:
      'Power and toughness modifications are applied in this layer, broken into five sublayers applied in order (7a through 7e). This ensures consistent results when multiple P/T effects interact.',
    sublayers: [
      {
        id: SUBLAYER_IDS.CDA,
        name: 'Characteristic-Defining Abilities',
        description:
          'Abilities that define a creature\'s power and/or toughness as part of its characteristic, rather than setting a specific number. These are marked by a * in the P/T box.',
        examples: [
          {
            scenario: 'Tarmogoyf has power equal to the number of card types in all graveyards and toughness equal to that number plus 1. There are 4 card types in graveyards.',
            resolution:
              'In sublayer 7a, Tarmogoyf\'s CDA sets its P/T to 4/5.',
          },
        ],
      },
      {
        id: SUBLAYER_IDS.SET,
        name: 'P/T-Setting Effects',
        description:
          'Effects that set a creature\'s power and/or toughness to a specific value. These override earlier values but can be further modified by later sublayers.',
        examples: [
          {
            scenario: 'Humility sets all creatures to base 1/1. Tarmogoyf is on the battlefield with 4 card types in graveyards.',
            resolution:
              'In sublayer 7a, Tarmogoyf\'s CDA would make it 4/5. In sublayer 7b, Humility sets it to 1/1. The 1/1 overwrites the 4/5.',
          },
        ],
      },
      {
        id: SUBLAYER_IDS.MODIFY,
        name: 'P/T-Modifying Effects',
        description:
          'Effects that modify (add or subtract) power and/or toughness without setting them to a specific value. Includes effects like "gets +2/+2" or "gets -1/-1 until end of turn."',
        examples: [
          {
            scenario: 'A 2/2 Bear Cub is enchanted with Giant Growth (+3/+3) and Weakness (-2/-2).',
            resolution:
              'In sublayer 7c, both modifications apply: 2+3-2 = 3 power, 2+3-2 = 3 toughness. Bear Cub is a 3/3.',
          },
        ],
      },
      {
        id: SUBLAYER_IDS.COUNTERS,
        name: 'Counters',
        description:
          'Power and toughness modifications from counters on the creature. Each +1/+1 counter adds 1 to both power and toughness. Each -1/-1 counter subtracts 1 from both.',
        examples: [
          {
            scenario: 'A 3/3 creature has two +1/+1 counters and one -1/-1 counter on it.',
            resolution:
              'In sublayer 7d, counters modify P/T: 3+2-1 = 4 power, 3+2-1 = 4 toughness. (Note: SBAs would also remove one pair of +1/+1 and -1/-1 counters, leaving one +1/+1 counter, but the math result is the same: 3+1 = 4/4.)',
          },
        ],
      },
      {
        id: SUBLAYER_IDS.SWITCH,
        name: 'P/T Switching Effects',
        description:
          'Effects that switch a creature\'s power and toughness. These are always applied last so they swap the final calculated values from all previous sublayers.',
        examples: [
          {
            scenario: 'A 1/4 creature is enchanted with Inside Out ("Switch target creature\'s power and toughness until end of turn") and also has a +1/+1 counter.',
            resolution:
              'Sublayers 7a-7d calculate P/T as 1+1 = 2 power, 4+1 = 5 toughness (2/5). In sublayer 7e, Inside Out switches P/T, making the creature a 5/2.',
          },
        ],
      },
    ],
    examples: [
      {
        scenario: 'Humility (all creatures are 1/1 and lose all abilities) is on the battlefield. A player casts Giant Growth (+3/+3) on their Grizzly Bears (2/2).',
        resolution:
          'Layer 6: Humility removes all abilities. Layer 7b: Humility sets base P/T to 1/1. Layer 7c: Giant Growth adds +3/+3. Final result: 4/4 creature with no abilities until end of turn.',
      },
    ],
  },
];

module.exports = {
  LAYER_NUMBERS,
  SUBLAYER_IDS,
  LAYERS,
};
